import { Field, ITextInputProps, TextInput } from 'Components/Core';
import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { PrefixNameFunction } from 'Components/Core/InjectedPropsContext/NamePrefixModifier/types';
import { actions } from 'Data/ReduxViewModels/Barcode';
import { hasEmptyValue, hasValue, valueOrDefault } from 'Lib/Any';
import { Guid } from 'Lib/Guid';
import { pipe } from 'Lib/Hooks/Selectors';
import { parse } from 'Lib/Object/Path/shared';
import deepEquals from 'Lib/Object/deepEquals';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { tabbable } from 'tabbable';
import './TareId.scss';
import { ICoreFieldProps } from './types';

interface ITareIdProps extends ICoreFieldProps, ITextInputProps {
  prefixName?: PrefixNameFunction;
  inGrid?: boolean;
}
const moistureTinName = 'MoistureTin';
const associatedMassPaths = ['.ContainerMass', '.WetSoilAndContainerMass', '.DrySoilAndContainerMass'];

let idCounter = 0;

let TareId: React.FC<ITareIdProps> = ({ prefixName, name, disabled, inGrid, createWorksheetSelector }) => {
  const dispatch = useDispatch();
  const { state: locationState } = useLocation();
  const [fieldId] = useState(`tare-id-${idCounter++}`);
  const [focusUUID, setFocusedUUID] = useState<Guid | null>(locationState?.focusUUID);
  const [dryingTrayPath, setDryingTrayPath] = useState<string | null>(null);
  const activeWorkflow = useSelector((state) => state.barcode.activeWorkflow, deepEquals);
  const isBarcodePanelOpen = useSelector(pipe((state) => state.barcode.isBarcodePanelOpen, valueOrDefault(false)));
  const ref = useRef(null);

  const dryingTrayId = useSelector(createWorksheetSelector!(`${dryingTrayPath}.Id`)) as Guid;
  const associatedTestUUID = useSelector(
    createWorksheetSelector!(`${dryingTrayPath}.${moistureTinName}.AssociatedTestUUID`)
  );
  const isTareIdSet: boolean = useSelector(
    pipe(
      (state) => state.barcode.tareIdList,
      (list) => !!dryingTrayId && list.some((t) => t.id === dryingTrayId)
    )
  );

  useEffect(() => {
    if (name) setDryingTrayPath(parse(name).slice(0, -1).join('.') ?? '');
  }, [name]);

  useEffect(() => {
    window.history.replaceState({}, document.title);
  }, []);

  useEffect(() => {
    if (activeWorkflow?.ResourceId) {
      setFocusedUUID(activeWorkflow?.ResourceId);
    }
  }, [activeWorkflow?.ResourceId]);

  useEffect(() => {
    if (dryingTrayId)
      return () => {
        dispatch(actions.cleanUpTareId(dryingTrayId));
      };
  }, [dispatch, dryingTrayId]);

  useEffect(() => {
    if (dryingTrayId && !isTareIdSet && dryingTrayPath) {
      var index = undefined;
      if (document.documentElement) {
        var tareIds = tabbable(document.documentElement).filter((e) => e.id.startsWith('tare-id'));
        index = tareIds.findIndex((t) => t.id === fieldId);
      }
      dispatch(
        actions.initTareId({
          tareId: {
            id: dryingTrayId,
            path: prefixName!(`${dryingTrayPath}.${moistureTinName}`),
            value: prefixName!(name!),
          },
          index,
        })
      );
    }
  }, [dispatch, dryingTrayId, prefixName, isTareIdSet, dryingTrayPath, fieldId, name]);

  useEffect(() => {
    if (hasValue(focusUUID) && focusUUID === dryingTrayId && !isBarcodePanelOpen && dryingTrayPath) {
      setFocusedUUID(null);
      //A timeout is used in order to ensure focus is trapped in the field. The reason this is necessary, is that when a barcode is scanned via the panel,
      //the closing of the panel attempts to return focus to the app nav icons/buttons. We could prevent this by setting `returnFocusOnDeactivate` to false
      //in the FocusTrap but we would also lose the returning of focus when tabbing through the app nav panel and closing/opening panels.
      setTimeout(() => {
        if (!document.activeElement) return;
        // Using tabbable allows us to get an ordered set of fields and allows us to adjust where we start looking for wet/dry mass fields
        var elements = tabbable(document.documentElement) as any[];
        var start = elements.findIndex((t) => t.id === fieldId);
        var end = elements.findIndex((t, i) => t.id?.startsWith('tare-id') && i > start);
        // Remove as many fields as we can to limit the search and handle when multiple sets of drying trays have fields with the same "name".
        elements = [...elements.slice(start, end)]; // End might be -1 but that just excludes last element which would never be an issue.

        elements
          .find((t: any) => {
            for (var i = 0; i < associatedMassPaths.length; i++) {
              if (t?.name?.endsWith(`${associatedMassPaths[i]}.Display`) && hasEmptyValue(t?.value)) return true;
            }
            return false;
          })
          ?.focus();
      }, 100);
    }
  }, [dryingTrayId, focusUUID, isBarcodePanelOpen, dryingTrayPath, fieldId, dispatch]);

  const onFocus = () => {
    dispatch(actions.setFocusedTareId(dryingTrayId));
  };
  const onLoseFocus = () => {
    dispatch(actions.setFocusedTareId(null));
  };

  return (
    <div onFocus={onFocus} onBlur={onLoseFocus} className={associatedTestUUID === dryingTrayId ? 'TareId-withTin' : ''}>
      <Field
        id={fieldId}
        inGrid={inGrid}
        input={<TextInput />}
        focusRef={ref}
        name={name}
        maxLength={20}
        disabled={disabled}
      />
    </div>
  );
};

TareId = withInjectedProps(TareId);

export { TareId };
